/**
 * ViewLinkedCustomerQuickAction
 * -----------------------------------------------------------------------------
 * Atalho do FAB do chat que abre a visão 360º do contato da conversa.
 *
 * Regra (v2.0.0): o botão é FIXO — basta existir `contactId`. O próprio
 * `CustomerDetailsModalPremium` resolve internamente o vínculo com CRM:
 *   - contato COM cliente vinculado → abre o modal completo do Cliente
 *   - contato SEM cliente           → abre em "modo Contato" (Negócios/Chat/Tarefas)
 *
 * @version 2.0.0
 * RELEASE: 2026-08-07 v2.0.0 - Botão fixo (sem checagem de vínculo/crm_enabled);
 *          delega a resolução Cliente x Contato ao modal (contactId).
 * RELEASE: 2026-06-03 v1.0.0 - Criação do atalho "Ver cliente" no FAB.
 */

import { useState } from 'react';
import { UserSquare } from 'lucide-react';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ViewLinkedCustomerQuickAction');

interface ViewLinkedCustomerQuickActionProps {
  /** Contato da conversa atual. */
  contactId?: string;
}

export function ViewLinkedCustomerQuickAction({ contactId }: ViewLinkedCustomerQuickActionProps) {
  const [open, setOpen] = useState(false);

  // Única condição de visibilidade: existir contato na conversa.
  if (!contactId) return null;

  return (
    <>
      <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  logger.debug('Abrindo modal de detalhes (contato/cliente)', { contactId });
                  setOpen(true);
                }}
                aria-label="Ver detalhes"
                className="h-11 w-11 rounded-full flex items-center justify-center text-foreground hover:text-primary transition-colors"
              >
                <UserSquare className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Ver detalhes</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Modal montado apenas quando aberto — resolve Cliente x Contato internamente */}
      {open && (
        <CustomerDetailsModalPremium
          customerId={null}
          contactId={contactId}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </>
  );
}
